import { makeId } from './defaults'
import { parseProjectDocumentValue } from './projectStore'
import type { CandidateLens, ProjectDocumentV1 } from './types'

export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') {
        field += '"'
        index += 1
      } else if (character === '"') {
        quoted = false
      } else {
        field += character
      }
    } else if (character === '"') {
      quoted = true
    } else if (character === ',') {
      row.push(field)
      field = ''
    } else if (character === '\n') {
      row.push(field.replace(/\r$/, ''))
      if (row.some((value) => value.trim() !== '')) rows.push(row)
      row = []
      field = ''
    } else {
      field += character
    }
  }
  if (quoted) throw new Error('CSV contains an unclosed quoted field.')
  row.push(field.replace(/\r$/, ''))
  if (row.some((value) => value.trim() !== '')) rows.push(row)
  return rows
}

export function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value)
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function optionalNumber(value: string, row: number, field: string, errors: string[]): number | null {
  if (!value.trim()) return null
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    errors.push(`Row ${row}: ${field} must be a finite number.`)
    return null
  }
  return parsed
}

const candidateHeaders = [
  'name',
  'manufacturer',
  'mode',
  'pitch_mm',
  'lpi',
  'focal_length_mm',
  'focal_reference',
  'radius_mm',
  'refractive_index',
  'center_thickness_mm',
  'fill_factor',
  'pitch_tolerance_mm',
  'focal_tolerance_mm',
  'radius_tolerance_mm',
  'thickness_tolerance_mm',
  'lateral_offset_mm',
  'notes',
]

export function exportCandidatesCsv(candidates: CandidateLens[]): string {
  const lines = [candidateHeaders.map(csvCell).join(',')]
  candidates.forEach((candidate) => {
    lines.push(
      [
        candidate.name,
        candidate.manufacturer,
        candidate.mode,
        candidate.pitchMm,
        candidate.lpi,
        candidate.focalLengthMm,
        candidate.focalReference,
        candidate.radiusMm,
        candidate.refractiveIndex,
        candidate.centerThicknessMm,
        candidate.fillFactor,
        candidate.pitchToleranceMm,
        candidate.focalToleranceMm,
        candidate.radiusToleranceMm,
        candidate.thicknessToleranceMm,
        candidate.lateralOffsetMm,
        candidate.notes,
      ]
        .map(csvCell)
        .join(','),
    )
  })
  return `${lines.join('\n')}\n`
}

export function parseCandidatesCsv(text: string): {
  candidates: CandidateLens[] | null
  errors: string[]
} {
  const errors: string[] = []
  let rows: string[][]
  try {
    rows = parseCsv(text)
  } catch (error) {
    return { candidates: null, errors: [error instanceof Error ? error.message : String(error)] }
  }
  if (rows.length < 2) return { candidates: null, errors: ['CSV must contain a header and at least one row.'] }
  const headers = rows[0].map((value) => value.trim().toLowerCase())
  const indexOf = (name: string) => headers.indexOf(name)
  for (const required of ['name', 'mode']) {
    if (indexOf(required) < 0) errors.push(`Missing required CSV column: ${required}.`)
  }
  if (errors.length) return { candidates: null, errors }
  const valueAt = (row: string[], name: string) => {
    const index = indexOf(name)
    return index >= 0 ? row[index] ?? '' : ''
  }
  const candidates = rows.slice(1).map((row, rowIndex): CandidateLens => {
    const line = rowIndex + 2
    if (row.length > headers.length) errors.push(`Row ${line}: more fields than header columns.`)
    const name = valueAt(row, 'name').trim()
    const mode = valueAt(row, 'mode').trim().toLowerCase()
    if (!name) errors.push(`Row ${line}: name is required.`)
    if (mode !== 'thin' && mode !== 'thick') errors.push(`Row ${line}: mode must be thin or thick.`)
    const focalReferenceRaw = valueAt(row, 'focal_reference').trim()
    const focalReference = [
      'effective-at-plane',
      'back-from-plane',
      'vendor-unspecified',
    ].includes(focalReferenceRaw)
      ? (focalReferenceRaw as CandidateLens['focalReference'])
      : 'vendor-unspecified'
    if (focalReferenceRaw && focalReference === 'vendor-unspecified' && focalReferenceRaw !== 'vendor-unspecified') {
      errors.push(`Row ${line}: invalid focal_reference.`)
    }
    const candidate: CandidateLens = {
      id: makeId('candidate'),
      name,
      manufacturer: valueAt(row, 'manufacturer'),
      mode: mode === 'thick' ? 'thick' : 'thin',
      pitchMm: optionalNumber(valueAt(row, 'pitch_mm'), line, 'pitch_mm', errors),
      lpi: optionalNumber(valueAt(row, 'lpi'), line, 'lpi', errors),
      focalLengthMm: optionalNumber(
        valueAt(row, 'focal_length_mm'),
        line,
        'focal_length_mm',
        errors,
      ),
      focalReference,
      radiusMm: optionalNumber(valueAt(row, 'radius_mm'), line, 'radius_mm', errors),
      refractiveIndex: optionalNumber(
        valueAt(row, 'refractive_index'),
        line,
        'refractive_index',
        errors,
      ),
      centerThicknessMm: optionalNumber(
        valueAt(row, 'center_thickness_mm'),
        line,
        'center_thickness_mm',
        errors,
      ),
      fillFactor: optionalNumber(valueAt(row, 'fill_factor'), line, 'fill_factor', errors),
      pitchToleranceMm: optionalNumber(
        valueAt(row, 'pitch_tolerance_mm'),
        line,
        'pitch_tolerance_mm',
        errors,
      ),
      focalToleranceMm: optionalNumber(
        valueAt(row, 'focal_tolerance_mm'),
        line,
        'focal_tolerance_mm',
        errors,
      ),
      radiusToleranceMm: optionalNumber(
        valueAt(row, 'radius_tolerance_mm'),
        line,
        'radius_tolerance_mm',
        errors,
      ),
      thicknessToleranceMm: optionalNumber(
        valueAt(row, 'thickness_tolerance_mm'),
        line,
        'thickness_tolerance_mm',
        errors,
      ),
      lateralOffsetMm: optionalNumber(
        valueAt(row, 'lateral_offset_mm'),
        line,
        'lateral_offset_mm',
        errors,
      ),
      notes: valueAt(row, 'notes'),
    }
    if (!candidate.pitchMm && !candidate.lpi) {
      errors.push(`Row ${line}: pitch_mm or lpi is required.`)
    }
    return candidate
  })
  return errors.length ? { candidates: null, errors } : { candidates, errors: [] }
}

export function exportCandidatesJson(candidates: CandidateLens[]): string {
  return JSON.stringify(
    {
      schema: 'lenticular-display-lab/candidates',
      version: 1,
      units: { distance: 'mm', lineDensity: 'LPI' },
      candidates: candidates.map(({ simulated: _simulated, monteCarlo: _monteCarlo, ...candidate }) =>
        candidate,
      ),
    },
    null,
    2,
  )
}

export function parseCandidatesJson(text: string): {
  candidates: CandidateLens[] | null
  errors: string[]
} {
  try {
    const parsed = JSON.parse(text) as {
      schema?: string
      version?: number
      units?: { distance?: string }
      candidates?: unknown[]
    }
    if (parsed.schema !== 'lenticular-display-lab/candidates' || parsed.version !== 1) {
      return { candidates: null, errors: ['Unsupported candidate JSON schema or version.'] }
    }
    if (parsed.units?.distance !== 'mm') {
      return { candidates: null, errors: ['Candidate JSON distance units must be mm.'] }
    }
    if (!Array.isArray(parsed.candidates) || !parsed.candidates.length) {
      return { candidates: null, errors: ['Candidate JSON contains no candidates.'] }
    }
    const errors: string[] = []
    const candidates = parsed.candidates.map((raw, index) => {
      const candidate = raw as Partial<CandidateLens>
      if (!candidate.name) errors.push(`Candidate ${index + 1}: name is required.`)
      if (candidate.mode !== 'thin' && candidate.mode !== 'thick') {
        errors.push(`Candidate ${index + 1}: mode must be thin or thick.`)
      }
      if (!Number.isFinite(candidate.pitchMm) && !Number.isFinite(candidate.lpi)) {
        errors.push(`Candidate ${index + 1}: pitchMm or lpi is required.`)
      }
      return {
        id: makeId('candidate'),
        name: candidate.name ?? '',
        manufacturer: candidate.manufacturer ?? '',
        mode: candidate.mode === 'thick' ? 'thick' : 'thin',
        pitchMm: Number.isFinite(candidate.pitchMm) ? candidate.pitchMm! : null,
        lpi: Number.isFinite(candidate.lpi) ? candidate.lpi! : null,
        focalLengthMm: Number.isFinite(candidate.focalLengthMm) ? candidate.focalLengthMm! : null,
        focalReference: candidate.focalReference ?? 'vendor-unspecified',
        radiusMm: Number.isFinite(candidate.radiusMm) ? candidate.radiusMm! : null,
        refractiveIndex: Number.isFinite(candidate.refractiveIndex)
          ? candidate.refractiveIndex!
          : null,
        centerThicknessMm: Number.isFinite(candidate.centerThicknessMm)
          ? candidate.centerThicknessMm!
          : null,
        fillFactor: Number.isFinite(candidate.fillFactor) ? candidate.fillFactor! : null,
        pitchToleranceMm: Number.isFinite(candidate.pitchToleranceMm)
          ? candidate.pitchToleranceMm!
          : null,
        focalToleranceMm: Number.isFinite(candidate.focalToleranceMm)
          ? candidate.focalToleranceMm!
          : null,
        radiusToleranceMm: Number.isFinite(candidate.radiusToleranceMm)
          ? candidate.radiusToleranceMm!
          : null,
        thicknessToleranceMm: Number.isFinite(candidate.thicknessToleranceMm)
          ? candidate.thicknessToleranceMm!
          : null,
        lateralOffsetMm: Number.isFinite(candidate.lateralOffsetMm)
          ? candidate.lateralOffsetMm!
          : null,
        notes: candidate.notes ?? '',
      } satisfies CandidateLens
    })
    return errors.length ? { candidates: null, errors } : { candidates, errors: [] }
  } catch (error) {
    return {
      candidates: null,
      errors: [error instanceof Error ? error.message : 'Invalid JSON.'],
    }
  }
}

export function exportProjectJson(project: ProjectDocumentV1): string {
  return JSON.stringify(project, null, 2)
}

export function parseProjectJson(text: string): {
  project: ProjectDocumentV1 | null
  errors: string[]
} {
  try {
    return parseProjectDocumentValue(JSON.parse(text) as unknown)
  } catch (error) {
    return {
      project: null,
      errors: [error instanceof Error ? error.message : 'Invalid project JSON.'],
    }
  }
}

export function downloadText(filename: string, content: string, type: string): void {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
